import axios from "axios";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ─── Base URL ────────────────────────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_API_URL || "/api";

if (process.env.NODE_ENV === "production" && BASE_URL.startsWith("http://")) {
  throw new Error(
    `REACT_APP_API_URL must use HTTPS in production. Got: ${BASE_URL}`
  );
}

// ─── Token helpers ───────────────────────────────────────────────────────────
export const getAccessToken = () => localStorage.getItem("token") || null;

export const setAccessToken = (token) => localStorage.setItem("token", token);

export const clearAuthStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("user");
  localStorage.removeItem("company");
  localStorage.removeItem("site");
  document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
};

// ─── Axios instance ──────────────────────────────────────────────────────────
// Custom paramsSerializer: axios's default array format is `key[]=a&key[]=b`,
// but every MS1 endpoint that takes a list filter (sites, zones, cameras,
// categories, roles, ...) declares it as `List[str] = Query(None)`, which
// FastAPI only recognizes as repeated plain keys: `key=a&key=b`. Without this,
// list-valued filters are silently ignored server-side -- no error, the
// param just doesn't match anything and the filter appears to do nothing.
const serializeParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined) searchParams.append(key, item);
      });
    } else {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

export const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  paramsSerializer: { serialize: serializeParams },
});

// ─── Proactive token refresh ─────────────────────────────────────────────────
// The access token is a fixed-lifetime JWT (60 min, see MS1
// auth_service.py:_create_access_token) with no sliding expiry. Without this,
// a session left open and in active use (this dashboard is meant to run on a
// screen all day) gets hard-logged-out by the generic-401 handler below the
// moment the token ages out, wiping whatever page state the user had. This
// refreshes it in the background once it's close to expiring, before that
// can happen.
const REFRESH_BUFFER_MS = 10 * 60 * 1000; // refresh once under 10 min remain

const isTokenNearExpiry = (token) => {
  try {
    const { exp } = jwt_decode(token);
    return exp * 1000 - Date.now() < REFRESH_BUFFER_MS;
  } catch {
    // Malformed/unparseable token: let the request go through as-is: the
    // response interceptor's generic-401 handling below still applies.
    return false;
  }
};

let refreshPromise = null;
const refreshAccessToken = () => {
  if (!refreshPromise) {
    // Deduped: N requests firing at once near expiry trigger one refresh
    // call, not N. Uses a bare axios call (not the API instance) so this
    // never re-enters this same request interceptor.
    refreshPromise = axios
      .post(`${BASE_URL}/auth/refresh`, null, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      })
      .then(({ data }) => {
        if (data?.token) setAccessToken(data.token);
      })
      .catch(() => {
        // Refresh failing (token already expired, account suspended, network
        // blip) just means the original request proceeds with the old token
        // and gets the normal 401 handling below if it's truly invalid.
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// ─── Request interceptor ─────────────────────────────────────────────────────
API.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    if (token && isTokenNearExpiry(token)) {
      await refreshAccessToken();
    }

    // Attach Bearer token
    const currentToken = getAccessToken();
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }

    // CSRF protection: echo XSRF-TOKEN cookie as a request header
    const xsrfToken = document.cookie
      .split("; ")
      .find((row) => row.startsWith("XSRF-TOKEN="))
      ?.split("=")[1];
    if (xsrfToken) {
      config.headers["X-XSRF-Token"] = xsrfToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Toast config ─────────────────────────────────────────────────────────────
const TOAST_OPTIONS = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "light",
};

const EXCLUDED_URLS = [
  "/users/signup",
  "/users/logout",
  "/users/login",
  "/notification",
  "/guide/update/",
  "/ai-chat/send",
];

const RESOURCE_LABELS = {
  users: "User",
  rules: "Rule",
  zones: "Zone",
  sites: "Site",
  companies: "Company",
};

const METHOD_LABELS = {
  post: "added",
  put: "updated",
  delete: "deleted",
};

const showSuccessToast = (method, url) => {
  if (EXCLUDED_URLS.some((excluded) => url.includes(excluded))) return;

  const action = METHOD_LABELS[method];
  if (!action) return;

  const resource =
    Object.entries(RESOURCE_LABELS).find(([key]) => url.includes(key))?.[1] ||
    "Record";

  toast.success(`${resource} successfully ${action}.`, TOAST_OPTIONS);
};

// ─── Auth redirect helpers ────────────────────────────────────────────────────
const AUTH_EXCLUDED_PATHS = [
  "/signup",
  "/signin",
  "/passwordRecovery",
  "/404",
  "/500",
  "/serveurError",
  "/access-denied",
  "/ms2", // standalone MS2 Control Center: has its own login, must not bounce to the webapp sign-in
];

const isAuthPage = () => AUTH_EXCLUDED_PATHS.includes(window.location.pathname);

// ─── Response interceptor ────────────────────────────────────────────────────
API.interceptors.response.use(
  (response) => {
    const { method, url, skipSuccessToast } = response.config;
    if (!skipSuccessToast) {
      showSuccessToast(method, url);
    }
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    // Session expired via cookie check (backend-specific error)
    if (detail === "Token not found in cookie" && !isAuthPage()) {
      toast.error("Session expired. Please login again.", TOAST_OPTIONS);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const message = encodeURIComponent("Session expired. Please login again.");
      window.location.href = `/signin?message=${message}`;
      return Promise.reject(error);
    }

    // Generic 401 — clear storage and redirect
    if (status === 401 && !isAuthPage()) {
      clearAuthStorage();
      API.post("/users/logout").catch(() => {}); // fire-and-forget
      window.location.href = "/signin";
    }

    return Promise.reject(error);
  }
);

// ─── Generic error handler ───────────────────────────────────────────────────
export const handleApiError = (error) => {
  const formatMessage = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    // FastAPI/Pydantic validation errors: detail is a list of
    // {loc, msg, type} objects (e.g. a required field that's missing) --
    // render as readable "Field: message" text instead of raw JSON.
    if (Array.isArray(value)) {
      const fields = value
        .map((item) => {
          const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
          const msg = item?.msg || formatMessage(item);
          return field && msg ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);
      return fields.length ? fields.join("; ") : null;
    }
    if (value.detail) return formatMessage(value.detail);
    if (value.message) return formatMessage(value.message);
    if (value.error) return formatMessage(value.error);
    try {
      return JSON.stringify(value);
    } catch {
      return null;
    }
  };

  let message;
  const responseData = error.response?.data;

  // Check if responseData is a string and looks like a JWT
  if (typeof responseData === 'string' && responseData.startsWith('eyJhbGciOi')) {
    message = "Authentication failed or session expired. Please log in again.";
  } else {
    // Original logic for extracting error message
    message =
      formatMessage(responseData?.detail) ||
      formatMessage(responseData?.camera) ||
      formatMessage(responseData?.message) ||
      formatMessage(responseData) ||
      error.message ||
      "An unexpected error occurred.";
  }

  return { error: message, data: null };
};